import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { CourseCompletionQuizResult } from "@/components/courseCompletionQuiz/CourseCompletionQuizResult";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingState } from "@/components/common/LoadingState";
import { useCourseCompletionQuizEvaluation } from "@/hooks/courseCompletionQuiz/useCourseCompletionQuizEvaluation";
import { useCourseTitleInHeader } from "@/hooks/courseCompletionQuiz/useCourseTitleInHeader";
import type { CompletionQuizResult } from "@/types/courseCompletionQuiz";

/** Dev only. `?preview=ready` shows this page after the 24-hour lock has ended. */
const resultForPreview = (
  result: CompletionQuizResult,
  preview: string | null
): CompletionQuizResult => {
  if (!import.meta.env.DEV || preview !== "ready") return result;
  return { ...result, status: "startable", nextAttemptAt: null };
};

/** Evaluation of the last finished run of the course completion quiz. */
export const CourseCompletionQuizResultPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const { enrollment, result, screen, error, isLoading } =
    useCourseCompletionQuizEvaluation(enrollmentId);

  useCourseTitleInHeader(enrollment?.courseTitle);

  if (!enrollmentId) {
    return (
      <ErrorState
        title="Auswertung konnte nicht geladen werden"
        message="Keine Einschreibungs-ID in der URL"
      />
    );
  }
  if (error) {
    return (
      <ErrorState
        title="Auswertung konnte nicht geladen werden"
        message={error.message}
      />
    );
  }
  if (isLoading || !enrollment) {
    return <LoadingState message="Auswertung wird geladen..." />;
  }

  const quiz = enrollment.courseCompletionQuiz;

  // Nothing to evaluate yet: the quiz has not been started, or is still locked.
  if (!quiz || screen === "dashboard") {
    return <Navigate to={`/course/${enrollmentId}/dashboard`} replace />;
  }
  if (screen === "intro") {
    return <Navigate to={`/course/${enrollmentId}/final-quiz`} replace />;
  }

  if (screen === "wait" || !result) {
    return (
      <div className="flex flex-col items-center gap-4 py-10">
        <LoadingState message="Dein Quiz wird ausgewertet..." />
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate(`/course/${enrollmentId}/final-quiz/run`)}
        >
          Zurück zum Quiz
        </Button>
      </div>
    );
  }

  return (
    <CourseCompletionQuizResult
      result={resultForPreview(result, searchParams.get("preview"))}
      summary={quiz}
      courseTitle={enrollment.courseTitle}
      firstName={enrollment.userFirstName ?? undefined}
      onDashboard={() => navigate(`/course/${enrollmentId}/dashboard`)}
      onCourse={() => navigate(`/course/${enrollmentId}`)}
      onNextAttempt={() => navigate(`/course/${enrollmentId}/final-quiz`)}
    />
  );
};
